/**
 * Sandbox access: the customer's links, and the credentials issued to the
 * signed-in address.
 *
 * A secret is revealed once, ever. The list never carries it; the reveal
 * response does, and it is held in this component's state for as long as the
 * page is open and nowhere else. Not in storage, not in the address, not in
 * the console. A reload of the list replaces the list, not this state, so a
 * secret on screen stays on screen.
 *
 * Revealing needs the NDA. Without an acceptance on file the button opens the
 * undertaking instead, and after accepting the visitor clicks again. Nothing
 * is revealed as a side effect of accepting.
 *
 * Beside the one secret, a credential carries the operator's details: label
 * and value rows shown as plain text, each with a Copy button. A value that
 * starts with https:// is a link; anything else, an http:// proxy included,
 * stays text.
 */

import { useEffect, useRef, useState } from 'react';
import type {
  CredentialDetail,
  ListPortalAccessResponse,
  PortalCredential,
  PortalLink,
} from '@insuredhq/contract';

import { Notice } from './Notice';
import {
  ACCESS_CREDENTIALS_HEADING,
  ACCESS_HEADING,
  ACCESS_LEDE,
  ACCESS_LINKS_HEADING,
  COPIED_LABEL,
  COPY_LABEL,
  CREDENTIAL_KIND_LABELS,
  CREDENTIAL_MASK,
  CREDENTIAL_MASK_LABEL,
  CREDENTIAL_RETRIEVED,
  CREDENTIAL_UNAVAILABLE,
  GENERIC_FAILURE_MESSAGE,
  REVEALED_NOTE,
  REVEALING_LABEL,
  REVEAL_CANCEL,
  REVEAL_CONFIRM,
  REVEAL_CONFIRM_MESSAGE,
  REVEAL_LABEL,
  copiedLabelFor,
  copyLabelFor,
  credentialAvailableUntil,
  credentialRetrievedOn,
} from '../copy';
import { formatDate } from '../dates';
import type { RevealOutcome } from '../hooks/usePortal';

const COPIED_FEEDBACK_MS = 2000;

interface SandboxAccessProps {
  access: ListPortalAccessResponse | null;
  onReveal: (credentialId: string) => Promise<RevealOutcome>;
  onRequestNda: (credentialId: string) => void;
}

export function SandboxAccess({ access, onReveal, onRequestNda }: SandboxAccessProps) {
  if (access === null) return null;
  if (access.links.length === 0 && access.credentials.length === 0) return null;

  return (
    <section className="group access">
      <h2>{ACCESS_HEADING}</h2>
      <p className="lede">{ACCESS_LEDE}</p>

      {access.links.length === 0 ? null : (
        <>
          <h3 className="access-subhead">{ACCESS_LINKS_HEADING}</h3>
          <ul className="document-list access-list">
            {access.links.map((link) => (
              <LinkRow key={link.id} link={link} />
            ))}
          </ul>
        </>
      )}

      {access.credentials.length === 0 ? null : (
        <>
          <h3 className="access-subhead">{ACCESS_CREDENTIALS_HEADING}</h3>
          <ul className="document-list access-list">
            {access.credentials.map((credential) => (
              <CredentialRow
                key={credential.id}
                credential={credential}
                ndaAccepted={access.ndaAccepted}
                onReveal={onReveal}
                onRequestNda={onRequestNda}
              />
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

function LinkRow({ link }: { link: PortalLink }) {
  return (
    <li className="document">
      <div className="document-detail">
        <a className="access-link" href={link.url} target="_blank" rel="noopener noreferrer">
          {link.label}
        </a>
        {link.description === null || link.description.length === 0 ? null : (
          <p className="document-desc">{link.description}</p>
        )}
      </div>
    </li>
  );
}

type Phase =
  | { name: 'idle' }
  | { name: 'confirming' }
  | { name: 'revealing' }
  | { name: 'revealed'; secret: string }
  | { name: 'unavailable' };

interface CredentialRowProps {
  credential: PortalCredential;
  ndaAccepted: boolean;
  onReveal: (credentialId: string) => Promise<RevealOutcome>;
  onRequestNda: (credentialId: string) => void;
}

function CredentialRow({ credential, ndaAccepted, onReveal, onRequestNda }: CredentialRowProps) {
  const [phase, setPhase] = useState<Phase>({ name: 'idle' });
  const [failed, setFailed] = useState(false);

  const { details, secretLabel } = credential.publicFields;
  const revealable = credential.status === 'pending' && phase.name !== 'unavailable';

  const startReveal = () => {
    setFailed(false);
    if (!ndaAccepted) {
      onRequestNda(credential.id);
      return;
    }
    setPhase({ name: 'confirming' });
  };

  const confirmReveal = async () => {
    setPhase({ name: 'revealing' });
    const outcome = await onReveal(credential.id);
    switch (outcome.kind) {
      case 'revealed':
        setPhase({ name: 'revealed', secret: outcome.secret });
        return;
      case 'unavailable':
        setPhase({ name: 'unavailable' });
        return;
      case 'failed':
        setFailed(true);
        setPhase({ name: 'idle' });
        return;
      case 'nda-required':
      case 'session-ended':
        setPhase({ name: 'idle' });
        return;
    }
  };

  return (
    <li className="document credential">
      <div className="document-detail">
        <div className="credential-head">
          <h3>{credential.label}</h3>
          <span className="chip">{CREDENTIAL_KIND_LABELS[credential.kind]}</span>
        </div>

        <dl className="credential-fields">
          {details.map((detail, index) => (
            <DetailRow key={index} detail={detail} />
          ))}
          {phase.name === 'revealed' ? (
            <div className="credential-field">
              <dt>{secretLabel}</dt>
              <dd>
                <RevealedSecret secret={phase.secret} label={secretLabel} />
              </dd>
            </div>
          ) : revealable ? (
            <div className="credential-field">
              <dt>{secretLabel}</dt>
              <dd className="credential-value">
                <span className="credential-mask" aria-label={CREDENTIAL_MASK_LABEL}>
                  {CREDENTIAL_MASK}
                </span>
                {phase.name === 'idle' ? (
                  <button type="button" className="button-secondary button-compact" onClick={startReveal}>
                    {REVEAL_LABEL}
                  </button>
                ) : null}
              </dd>
            </div>
          ) : null}
        </dl>

        <CredentialStatus
          credential={credential}
          phase={phase}
          revealable={revealable}
          onConfirm={() => void confirmReveal()}
          onCancel={() => setPhase({ name: 'idle' })}
        />

        {failed ? <Notice message={GENERIC_FAILURE_MESSAGE} /> : null}
      </div>
    </li>
  );
}

interface CredentialStatusProps {
  credential: PortalCredential;
  phase: Phase;
  revealable: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

function CredentialStatus({
  credential,
  phase,
  revealable,
  onConfirm,
  onCancel,
}: CredentialStatusProps) {
  const confirmRef = useRef<HTMLButtonElement>(null);
  const confirming = phase.name === 'confirming';

  /* The Reveal button goes away when the confirmation appears; focus follows
   * it rather than falling back to the page. */
  useEffect(() => {
    if (confirming) confirmRef.current?.focus();
  }, [confirming]);

  if (phase.name === 'revealed') {
    return <p className="document-meta">{REVEALED_NOTE}</p>;
  }

  if (!revealable) {
    if (credential.status === 'retrieved' && phase.name !== 'unavailable') {
      return (
        <p className="document-meta">
          {credential.revealedAt === null
            ? CREDENTIAL_RETRIEVED
            : credentialRetrievedOn(formatDate(credential.revealedAt))}
        </p>
      );
    }
    return <p className="document-meta">{CREDENTIAL_UNAVAILABLE}</p>;
  }

  if (phase.name === 'confirming' || phase.name === 'revealing') {
    const revealing = phase.name === 'revealing';
    return (
      <div className="credential-confirm">
        <p>{REVEAL_CONFIRM_MESSAGE}</p>
        <div className="credential-actions">
          <button
            type="button"
            className="button-secondary button-compact"
            onClick={onCancel}
            disabled={revealing}
          >
            {REVEAL_CANCEL}
          </button>
          <button
            ref={confirmRef}
            type="button"
            className="button-primary button-compact"
            onClick={onConfirm}
            disabled={revealing}
          >
            {revealing ? REVEALING_LABEL : REVEAL_CONFIRM}
          </button>
        </div>
      </div>
    );
  }

  return <p className="document-meta">{credentialAvailableUntil(formatDate(credential.expiresAt))}</p>;
}

/** Only the literal https:// prefix makes a link. Not a URL check: anything else is text. */
export function isLinkValue(value: string): boolean {
  return value.startsWith('https://');
}

function DetailRow({ detail }: { detail: CredentialDetail }) {
  const valueRef = useRef<HTMLSpanElement>(null);

  /* No clipboard access: select the value so a manual copy is one keystroke away. */
  const selectValue = () => {
    const node = valueRef.current;
    const selection = window.getSelection();
    if (node !== null && selection !== null) selection.selectAllChildren(node);
  };

  return (
    <div className="credential-field">
      <dt>{detail.label}</dt>
      <dd className="credential-value">
        <span ref={valueRef} className="credential-value-text">
          {isLinkValue(detail.value) ? (
            <a className="credential-link" href={detail.value} target="_blank" rel="noopener noreferrer">
              {detail.value}
            </a>
          ) : (
            detail.value
          )}
        </span>
        <CopyButton text={detail.value} label={detail.label} onUnavailable={selectValue} />
      </dd>
    </div>
  );
}

function CopyButton({
  text,
  label,
  onUnavailable,
}: {
  text: string;
  label: string;
  onUnavailable: () => void;
}) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS);
    return () => window.clearTimeout(timer);
  }, [copied]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch {
      /* No clipboard access, say on a non secure origin. */
      onUnavailable();
    }
  };

  return (
    <button
      type="button"
      className="button-secondary button-compact"
      onClick={() => void copy()}
      aria-label={copied ? copiedLabelFor(label) : copyLabelFor(label)}
      aria-live="polite"
    >
      {copied ? COPIED_LABEL : COPY_LABEL}
    </button>
  );
}

function RevealedSecret({ secret, label }: { secret: string; label: string }) {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  /* No clipboard access: select the text so a manual copy is one keystroke away. */
  const selectSecret = () => {
    inputRef.current?.focus();
    inputRef.current?.select();
  };

  return (
    <span className="credential-secret">
      <input
        ref={inputRef}
        type="text"
        className="credential-secret-value"
        value={secret}
        readOnly
        aria-label={label}
        autoComplete="off"
        spellCheck={false}
        onFocus={(event) => event.currentTarget.select()}
      />
      <CopyButton text={secret} label={label} onUnavailable={selectSecret} />
    </span>
  );
}
